/**
 * Single source for /legislation list URL query ↔ filters, search, and table sort.
 * Used by LegislationPage so browser Back restores list state (#558).
 */

import type { BillFilters } from '../hooks/useBillFilters';

// Sentinel resolved server-side; never a wall-clock year (see useSessions.ts).
import { CURRENT_SESSION } from '../hooks/useSessions';

/** Where the search box applies: DB title/summary/number vs indexed bill document text (RAG keyword). */
export type LegislationSearchScope = 'metadata' | 'fulltext';

export const DEFAULT_SEARCH_SCOPE: LegislationSearchScope = 'metadata';

export const LEGISLATION_SORT_FIELDS = [
  'number',
  'status',
  'lastActivity',
  'introducedDate',
  'houseCommittee',
  'senateCommittee',
] as const;

export type LegislationSortField = (typeof LEGISLATION_SORT_FIELDS)[number];
export type LegislationSortDirection = 'asc' | 'desc';

export const DEFAULT_LEGISLATION_SORT_FIELD: LegislationSortField = 'introducedDate';
export const DEFAULT_LEGISLATION_SORT_DIR: LegislationSortDirection = 'desc';

const SORT_FIELD_SET = new Set<string>(LEGISLATION_SORT_FIELDS);

/**
 * Read a multi-select filter from the query string.
 *
 * Repeated params (`?subject=a&subject=b`) rather than a comma-joined value,
 * because subject and committee names contain commas. An old single-value URL
 * still parses — getAll returns a one-element array.
 *
 * `sentinels` are legacy "means unfiltered" values ('All subjects') that older
 * bookmarks still carry; they are dropped so state never holds one.
 */
function getMultiParam(
  searchParams: URLSearchParams,
  key: string,
  sentinels: string[] = []
): string[] {
  const skip = new Set(sentinels.map((s) => s.toLowerCase()));
  const out: string[] = [];
  for (const raw of searchParams.getAll(key)) {
    const value = raw.trim();
    if (!value) continue;
    if (skip.has(value.toLowerCase())) continue;
    if (!out.includes(value)) out.push(value);
  }
  return out;
}

/** Append one query param per selected value, or nothing when unfiltered. */
function setMultiParam(params: URLSearchParams, key: string, values: string[]): void {
  for (const value of values) {
    params.append(key, value);
  }
}

/** Lobbyist/client deep links span many sessions — do not default to current year only. */
export function resolveLegislationSessionFromParams(searchParams: URLSearchParams): string[] {
  if (searchParams.has('session')) {
    return getMultiParam(searchParams, 'session');
  }
  if (searchParams.get('lobbyist_id') || searchParams.get('client_id') || searchParams.get('client_name')) {
    return [];
  }
  return [CURRENT_SESSION];
}

export interface LegislationLocationState {
  filters: BillFilters;
  searchQuery: string;
  searchScope: LegislationSearchScope;
  sortBy: LegislationSortField;
  sortDirection: LegislationSortDirection;
}

function parseSortFromParams(searchParams: URLSearchParams): {
  sortBy: LegislationSortField;
  sortDirection: LegislationSortDirection;
} {
  const rawSort = searchParams.get('sortBy');
  const sortBy: LegislationSortField =
    rawSort && SORT_FIELD_SET.has(rawSort)
      ? (rawSort as LegislationSortField)
      : DEFAULT_LEGISLATION_SORT_FIELD;
  const rawDir = searchParams.get('sortDirection');
  const sortDirection: LegislationSortDirection =
    rawDir === 'asc' || rawDir === 'desc' ? rawDir : DEFAULT_LEGISLATION_SORT_DIR;
  return { sortBy, sortDirection };
}

/**
 * Parse `location.search` (with or without leading `?`) into list state.
 */
export function parseLegislationSearchParams(search: string): LegislationLocationState {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(q);

  const filters: BillFilters = {
    chamber: searchParams.get('chamber') || 'All',
    subject: getMultiParam(searchParams, 'subject', ['All subjects']),
    status: getMultiParam(searchParams, 'status'),
    sponsor: getMultiParam(searchParams, 'sponsor'),
    committee_id: getMultiParam(searchParams, 'committee_id'),
    committee: searchParams.get('committee') || '',
    legislator_id: searchParams.get('legislator_id') || '',
    bill_status: searchParams.get('bill_status') || '',
    includeIntroduced: searchParams.get('includeIntroduced') === 'true',
    includeAmended: searchParams.get('includeAmended') === 'true',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    bipartisan: searchParams.get('bipartisan') === 'true',
    hot: searchParams.get('hot') === 'true',
    lobbyist_id: searchParams.get('lobbyist_id') || '',
    lobbyist_name: searchParams.get('lobbyist_name') || '',
    lobbyist_status: searchParams.get('lobbyist_status') || '',
    client_id: searchParams.get('client_id') || '',
    client_name: searchParams.get('client_name') || '',
    trackingStatus: searchParams.get('trackingStatus') || '',
    session: resolveLegislationSessionFromParams(searchParams),
  };

  const { sortBy, sortDirection } = parseSortFromParams(searchParams);
  const searchQuery = searchParams.get('search') ?? '';
  const rawScope = searchParams.get('searchScope');
  const searchScope: LegislationSearchScope =
    rawScope === 'fulltext' ? 'fulltext' : 'metadata';

  return {
    filters,
    searchQuery,
    searchScope,
    sortBy,
    sortDirection,
  };
}

/**
 * Build `location.search` value (including `?` when non-empty) for the current list state.
 * Omits default values to keep URLs readable.
 */
export function buildLegislationSearchString(
  filters: BillFilters,
  searchQuery: string,
  sortBy: LegislationSortField,
  sortDirection: LegislationSortDirection,
  searchScope: LegislationSearchScope = DEFAULT_SEARCH_SCOPE
): string {
  const params = new URLSearchParams();

  if (filters.chamber && filters.chamber !== 'All') {
    params.set('chamber', filters.chamber);
  }
  setMultiParam(params, 'subject', filters.subject);
  setMultiParam(params, 'status', filters.status);
  setMultiParam(params, 'sponsor', filters.sponsor);
  const committeeIds = filters.committee_id.filter((id) => /^\d+$/.test(id));
  if (committeeIds.length > 0) {
    setMultiParam(params, 'committee_id', committeeIds);
  } else if (filters.committee) {
    params.set('committee', filters.committee);
  }
  if (filters.legislator_id) {
    params.set('legislator_id', filters.legislator_id);
  }
  if (filters.bill_status) {
    params.set('bill_status', filters.bill_status);
  }
  if (filters.includeIntroduced) {
    params.set('includeIntroduced', 'true');
  }
  if (filters.includeAmended) {
    params.set('includeAmended', 'true');
  }
  if (filters.startDate) {
    params.set('startDate', filters.startDate);
  }
  if (filters.endDate) {
    params.set('endDate', filters.endDate);
  }
  if (filters.bipartisan) {
    params.set('bipartisan', 'true');
  }
  if (filters.hot) {
    params.set('hot', 'true');
  }
  if (filters.lobbyist_id) {
    params.set('lobbyist_id', filters.lobbyist_id);
  }
  if (filters.lobbyist_name) {
    params.set('lobbyist_name', filters.lobbyist_name);
  }
  if (filters.lobbyist_status && filters.lobbyist_status !== 'all') {
    params.set('lobbyist_status', filters.lobbyist_status);
  }
  if (filters.client_id) {
    params.set('client_id', filters.client_id);
  }
  if (filters.client_name) {
    params.set('client_name', filters.client_name);
  }
  if (filters.trackingStatus) {
    params.set('trackingStatus', filters.trackingStatus);
  }
  // Always include session when set so URL matches deep links (?session=2026&committee_id=…)
  // and the sync effect does not constantly replace the query string (reduces flicker).
  setMultiParam(params, 'session', filters.session);
  if (searchQuery) {
    params.set('search', searchQuery);
  }
  if (searchScope === 'fulltext') {
    params.set('searchScope', 'fulltext');
  }

  if (
    sortBy !== DEFAULT_LEGISLATION_SORT_FIELD ||
    sortDirection !== DEFAULT_LEGISLATION_SORT_DIR
  ) {
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
  }

  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * Canonical form for comparing query strings (key order independent).
 */
export function normalizeLegislationSearchString(search: string): string {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
  const next = new URLSearchParams(sorted);
  const s = next.toString();
  return s ? `?${s}` : '';
}
